import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [employee, setEmployee] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    if (token) {
      fetchEmployee();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchEmployee = async () => {
    try {
      console.log('=== AUTH CONTEXT: Fetching current user ===');
      console.log('Token:', token ? `${token.substring(0, 10)}...` : 'No token');
      
      const response = await authAPI.getCurrentUser();
      console.log('Current user response:', response.data);
      
      setEmployee(response.data.employee);
      
      // Log permissions for debugging
      if (response.data.employee?.permissions) {
        console.log('User permissions:', response.data.employee.permissions);
      }
    } catch (error) {
      console.error('Failed to fetch employee:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Handle authentication errors
      if (error.response?.status === 401) {
        console.log('Authentication error, clearing invalid token');
        // Clear invalid token
        localStorage.removeItem('token');
        setToken(null);
        setEmployee(null);
        setLoading(false);
        // Force redirect to login
        window.location.href = '/login';
      } else {
        // For other errors, just set loading to false without logging out
        console.log('Non-authentication error, staying logged in');
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      
      if (response.data.requiresVerification) {
        setRequiresVerification(true);
        return { success: false, requiresVerification: true, email: response.data.email };
      }

      const { token: newToken, employee: employeeData } = response.data;
      
      setToken(newToken);
      setEmployee(employeeData);
      localStorage.setItem('token', newToken);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const sendOTP = async (email) => {
    try {
      await authAPI.sendOTP({ email });
      return { success: true };
    } catch (error) {
      console.error('Send OTP error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to send OTP' 
      };
    }
  };

  const verifyOTP = async (email, otp) => {
    try {
      await authAPI.verifyOTP({ email, otp });
      setRequiresVerification(false);
      return { success: true };
    } catch (error) {
      console.error('Verify OTP error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to verify OTP' 
      };
    }
  };

  const logout = () => {
    console.log('🔄 Logging out...');
    setEmployee(null);
    setToken(null);
    setRequiresVerification(false);
    localStorage.removeItem('token');
    
    // Force refresh to clear any cached data
    window.location.href = '/login';
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      return { success: true };
    } catch (error) {
      console.error('Change password error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to change password' 
      };
    }
  };

  const hasPermission = (permission) => {
    if (!employee) {
      console.log('hasPermission: No employee data');
      return false;
    }
    
    // Developer role gets default access to all product groups permissions
    const isDeveloper = employee.role === 'developer' || employee.email === 'development@farmindie.com';
    if (isDeveloper && permission.startsWith('product_groups_')) {
      console.log(`✅ DEVELOPER DEFAULT ACCESS granted for "${permission}"`);
      return true;
    }
    
    // Check specific permission in the permissions array
    const permissions = employee.permissions || [];
    console.log(`hasPermission: Checking permission "${permission}". Employee has ${permissions.length} permissions.`);
    console.log(`hasPermission: User permissions:`, permissions.map(p => p.name).join(', '));
    
    const hasAccess = permissions.some(p => p.name === permission);
    console.log(`hasPermission: Result for "${permission}": ${hasAccess ? '✅ GRANTED' : '❌ DENIED'}`);
    
    return hasAccess;
  };

  const hasRole = (roles) => {
    if (!employee) return false;
    return Array.isArray(roles) ? roles.includes(employee.role) : employee.role === roles;
  };

  const value = {
    employee,
    token,
    loading,
    requiresVerification,
    login,
    logout,
    sendOTP,
    verifyOTP,
    changePassword,
    hasPermission,
    hasRole,
    isAuthenticated: !!employee,
    theme,
    toggleTheme
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 